/**
 * PrivacyPage.js
 * Privacy Policy conforme GDPR - Regolamento UE 2016/679
 * Legge i dati aziendali da Firebase settings.
 *
 * Questa pagina è raggiungibile in due modi:
 *  1. /:companySlug/privacy — dal sito pubblico di una specifica azienda
 *     (link nel footer di PublicCarList). Qui riceve `companyId` già risolto
 *     dallo slug e mostra i dati reali del Titolare per quell'azienda.
 *  2. /privacy — rotta generica, usata dalla vista aggregata /account (dove
 *     il cliente può avere prenotazioni presso più aziende diverse, quindi
 *     non esiste un unico "Titolare" da mostrare). In questo caso niente
 *     companyId viene passato e la pagina mostra un'informativa generica
 *     della piattaforma, senza inventare/azzerare dati di una singola azienda.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { useSettings } from '../hooks/useSettings';
import { useCompanyPageSEO } from '../hooks/useCompanyPageSEO';
import PublicFooter from '../components/public/PublicFooter';
import BrandDefaultIcon from '../components/BrandDefaultIcon';

const PrivacyPage = ({ companyId = null, basePath = '', onBack }) => {
  const navigate = useNavigate();
  const { settings } = useSettings(companyId);
  const az = companyId ? (settings?.azienda || {}) : {};
  const si = companyId ? (settings?.sito || {}) : {};

  const modalitaGenerica = !companyId;
  const accentColor = modalitaGenerica ? '#2563EB' : (si.coloreAccento || '#2563EB');
  // Titolare del trattamento: per correttezza legale usa la ragione sociale,
  // non il nome commerciale mostrato sul sito (possono differire).
  const titolare = modalitaGenerica
    ? 'NoleggioinCloud'
    : (az.ragioneSociale || 'Ragione sociale non impostata');
  // Nome mostrato nell'header: quello che il cliente riconosce dal sito.
  const displayName = modalitaGenerica ? 'NoleggioinCloud' : (si.nomeSito || titolare);
  const indirizzo = [az.indirizzo, az.citta].filter(Boolean).join(', ') || '—';
  const piva     = az.piva || '';
  const email    = az.email || '';
  const telefono = az.telefono || '';
  const oggi     = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });

  // SEO: title e canonical propri (prima ereditava quelli della home piattaforma).
  useCompanyPageSEO({
    enabled: modalitaGenerica || !!(si.nomeSito || az.ragioneSociale),
    title: `Privacy Policy — ${displayName}`,
    description: `Informativa sul trattamento dei dati personali di ${displayName}.`,
  });

  // Se non viene passato un onBack esplicito (es. rotta generica /privacy
  // raggiunta da /account), torniamo semplicemente indietro nella cronologia.
  const handleBack = onBack || (() => navigate(-1));

  return (
    <div className="min-h-screen" style={{ background: '#F5F6FA' }}>

      {/* Header — stesso stile della home/contatti per coerenza visiva */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {(si.logoSito || az.logoAzienda) ? (
              <img
                src={si.logoSito || az.logoAzienda}
                alt={displayName}
                className="h-7 sm:h-8 w-auto max-w-[140px] object-contain"
              />
            ) : (
              <BrandDefaultIcon style={{ color: accentColor }} />
            )}
            <span className="text-base sm:text-lg font-black tracking-tight leading-none" style={{ color: accentColor }}>
              {displayName}
            </span>
          </div>
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 px-4 py-2 border font-semibold text-sm hover:bg-slate-50 rounded-full transition"
            style={{ borderColor: accentColor, color: accentColor }}
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla Home
          </button>
        </div>
      </div>

      {/* Contenuto */}
      <div className="max-w-3xl mx-auto px-4 py-10">

        {/* Titolo */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${accentColor}1A` }}>
            <Shield className="w-7 h-7" style={{ color: accentColor }} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Privacy Policy</h1>
            <p className="text-sm text-slate-500">Informativa ai sensi degli artt. 13-14 GDPR – Reg. UE 2016/679 · Aggiornata: {oggi}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8 space-y-8 text-gray-700 text-sm leading-relaxed">

          {/* 1 */}
          <section>
            <h2 className="text-base font-bold text-gray-900 mb-3">1. Titolare del Trattamento</h2>
            <p>
              Il Titolare del trattamento dei dati personali è <strong>{titolare}</strong>
              {!modalitaGenerica && indirizzo !== '—' && <>, con sede in <strong>{indirizzo}</strong></>}
              {!modalitaGenerica && piva && <>, P.IVA <strong>{piva}</strong></>}.
              {modalitaGenerica && (
                <> Per le prenotazioni effettuate presso una specifica autonoleggio partner, il Titolare del trattamento dei relativi dati è l'autonoleggio stesso: consulta l'informativa privacy dal sito di quella specifica azienda.</>
              )}
            </p>
            {(email || telefono) && (
              <p className="mt-2">
                Contatti del Titolare:{' '}
                {email && <><strong>Email:</strong> {email}{telefono ? ' · ' : ''}</>}
                {telefono && <><strong>Tel:</strong> {telefono}</>}
              </p>
            )}
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-base font-bold text-gray-900 mb-3">2. Dati Raccolti</h2>
            <p>Raccogliamo i seguenti dati personali:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-gray-600">
              <li>Dati anagrafici: nome, cognome, luogo e data di nascita, codice fiscale</li>
              <li>Dati di contatto: indirizzo email, numero di telefono, indirizzo di residenza</li>
              <li>Dati della patente di guida: numero, tipo, ente rilasciante, data rilascio e scadenza, immagini fronte/retro</li>
              <li>Dati di pagamento: gestiti in modo sicuro tramite Stripe (non conserviamo dati della carta)</li>
              <li>Dati di prenotazione: veicolo scelto, date, importo, stato della prenotazione</li>
              <li>Dati di navigazione: indirizzo IP, browser, dispositivo (raccolti automaticamente)</li>
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-base font-bold text-gray-900 mb-3">3. Finalità e Base Giuridica</h2>
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold text-gray-800">Esecuzione del contratto (art. 6.1.b GDPR)</p>
                <p className="text-gray-600 mt-1">Gestione delle prenotazioni, emissione del contratto di noleggio, comunicazioni operative sul servizio.</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold text-gray-800">Obbligo legale (art. 6.1.c GDPR)</p>
                <p className="text-gray-600 mt-1">Adempimenti fiscali, contabili e obblighi di legge in materia di noleggio veicoli.</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold text-gray-800">Consenso (art. 6.1.a GDPR)</p>
                <p className="text-gray-600 mt-1">Invio di comunicazioni promozionali e newsletter, solo se esplicitamente acconsentito.</p>
              </div>
            </div>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-base font-bold text-gray-900 mb-3">4. Conservazione dei Dati</h2>
            <p>I dati personali vengono conservati per il tempo strettamente necessario alle finalità per cui sono stati raccolti:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-gray-600">
              <li>Dati contrattuali e di prenotazione: <strong>10 anni</strong> (obbligo fiscale)</li>
              <li>Foto della patente: <strong>1 anno</strong> dalla fine del noleggio</li>
              <li>Dati di navigazione: <strong>12 mesi</strong></li>
              <li>Dati per finalità promozionali: fino alla revoca del consenso</li>
            </ul>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-base font-bold text-gray-900 mb-3">5. Destinatari dei Dati</h2>
            <p>I dati possono essere comunicati a:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-gray-600">
              <li><strong>Google Firebase</strong> — archiviazione dati e autenticazione (server in Europa)</li>
              <li><strong>Stripe Inc.</strong> — elaborazione pagamenti con carta</li>
              <li><strong>Resend</strong> — invio email transazionali di piattaforma (es. comunicazioni del Platform Admin)</li>
              <li><strong>Brevo (Sendinblue SAS)</strong> — invio email di verifica account e conferma prenotazione</li>
              <li>Professionisti e consulenti del Titolare (commercialisti, avvocati) vincolati da riservatezza</li>
              <li>Autorità pubbliche, su richiesta di legge</li>
            </ul>
            <p className="mt-2">I dati non vengono venduti né ceduti a terzi per finalità di marketing.</p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-base font-bold text-gray-900 mb-3">6. Trasferimento Extra-UE</h2>
            <p>
              I dati sono archiviati su server Google Firebase nella regione <strong>Europa (europe-west1)</strong>.
              Stripe e Resend possono trasferire dati negli USA nel rispetto delle garanzie previste dal GDPR
              (Standard Contractual Clauses). Brevo (Sendinblue SAS) è una società con sede in Francia e tratta
              i dati all'interno dell'Unione Europea.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-base font-bold text-gray-900 mb-3">7. I Tuoi Diritti</h2>
            <p>Ai sensi degli artt. 15-22 GDPR hai diritto di:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-gray-600">
              <li><strong>Accesso</strong> — ottenere copia dei tuoi dati personali</li>
              <li><strong>Rettifica</strong> — correggere dati inesatti o incompleti</li>
              <li><strong>Cancellazione</strong> — richiedere la cancellazione ("diritto all'oblio")</li>
              <li><strong>Limitazione</strong> — limitare il trattamento in determinati casi</li>
              <li><strong>Portabilità</strong> — ricevere i dati in formato strutturato</li>
              <li><strong>Opposizione</strong> — opporti al trattamento per finalità promozionali</li>
              <li><strong>Revoca del consenso</strong> — in qualsiasi momento, senza pregiudizio</li>
            </ul>
            <p className="mt-3">
              Per esercitare i tuoi diritti scrivi a{' '}
              {email
                ? <><strong>{email}</strong> o chiama il <strong>{telefono}</strong>.</>
                : 'al Titolare del trattamento.'
              }
              {' '}Hai inoltre diritto di proporre reclamo al <strong>Garante per la protezione dei dati personali</strong> (www.garanteprivacy.it).
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-base font-bold text-gray-900 mb-3">8. Cookie e Tecnologie di Tracciamento</h2>
            <p className="mb-2">
              Il sito utilizza cookie tecnici strettamente necessari al funzionamento (autenticazione, preferenza lingua).
              Non utilizziamo cookie di profilazione o pubblicitari propri.
            </p>
            <p className="mb-2">
              Il sito si avvale inoltre dei seguenti servizi di terze parti, che possono impostare propri cookie:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600 mb-2">
              <li>
                <strong>Stripe (Stripe Inc. / Stripe Payments Europe)</strong> — elaborazione pagamenti con carta; cookie strettamente necessari al completamento della transazione richiesta dall'utente.
              </li>
              <li>
                <strong>Google Maps / Places (Google LLC)</strong> — ricerca e autocompilazione indirizzi; comporta il trasferimento di dati a Google LLC, anche extra-UE, e cookie propri del servizio mappe.
              </li>
              <li>
                <strong>Google Translate (se attivo)</strong> — cookie tecnici per ricordare la lingua selezionata.
              </li>
            </ul>
            <p className="text-xs text-gray-500">
              Informativa terze parti:{' '}
              <a href="https://stripe.com/it/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">Stripe Privacy</a> ·{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">Google Privacy</a>.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-base font-bold text-gray-900 mb-3">9. Modifiche alla Privacy Policy</h2>
            <p>
              Il Titolare si riserva di aggiornare questa informativa. Le modifiche sostanziali verranno
              comunicate tramite avviso in evidenza sul sito. La data di ultimo aggiornamento è indicata in cima alla pagina.
            </p>
            <p className="mt-2 text-xs text-gray-400 font-mono">
              Versione documento: v1.0 (Settembre 2026)
            </p>
          </section>

        </div>

        {!modalitaGenerica && (
          <p className="text-center text-xs text-gray-400 mt-8">
            {titolare} · {indirizzo}{piva ? ` · P.IVA ${piva}` : ''}
          </p>
        )}
      </div>

      {/* Footer condiviso, coerente con home e contatti — solo in modalità
          azienda: la rotta generica /privacy (aggregata, multi-azienda) non
          ha un singolo noleggio a cui attribuire link "Catalogo"/"Contatti". */}
      {!modalitaGenerica && (
        <PublicFooter
          displayName={displayName}
          slogan={si.slogan}
          logoSito={si.logoSito || az.logoAzienda || ''}
          footerSottotitolo={si.footerSottotitolo}
          accentColor={accentColor}
          basePath={basePath}
          ragioneSociale={az.ragioneSociale}
          piva={piva}
          indirizzo={indirizzo !== '—' ? indirizzo : ''}
          email={email}
          telefono={telefono}
        />
      )}
    </div>
  );
};

export default PrivacyPage;
